import { PageboxLegalViewer, usePageboxLegal } from "../PageboxLegal";
import { pageboxAuthPath } from "../pageboxNavigation";
import { PageboxAccountNotice } from "../PageboxAccountNotice";
// src/features/auth2/pages/AgreeTermsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { withBase } from "@/ui/Header";
import { PageShell } from "@/ui/PageShell";
import { usePageScale } from "@/ui/usePageScale";

import {
    APP_ID_SS_KEY,
    CHECKS_SS_KEY,
    buildAgreements,
    resolveConsentPolicy,
    type ConsentItem,
} from "../consentPolicy";

const BASE = {
    W: 1264,
    MARGIN_X: 108,
    BLOCK_W: 1048,

    ROW_PAD_Y: 22,
    ROW_BORDER: 1,

    TITLE_FZ: 44,

    BTN_W: 848,
    BTN_H: 124,

    GAP_Y: 24,
} as const;

function useScale() {
    const { s, px } = usePageScale({ baseW: BASE.W, max: 1.2 });
    const spx = px;
    return { s, px, spx };
}

function CheckRow({
                      id, checked, onChange, label, right, padY, fontPx, borderTop,
                  }: {
    id: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    label: React.ReactNode;
    right?: React.ReactNode;
    padY: number;
    fontPx: number;
    borderTop?: boolean;
}) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                paddingTop: padY,
                paddingBottom: padY,
                borderTop: borderTop ? `${BASE.ROW_BORDER}px solid #e5e5e5` : "none",
            }}
        >
            <label
                htmlFor={id}
                style={{ display: "flex", alignItems: "center", cursor: "pointer", flex: 1, minWidth: 0 }}
            >
                <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ width: fontPx, height: fontPx }}
                />
                <span style={{ marginLeft: 12, fontSize: fontPx, fontWeight: 800 }}>{label}</span>
            </label>

            {right ? <div style={{ marginLeft: 12 }}>{right}</div> : null}
        </div>
    );
}

type Checks = Record<string, boolean>;

/**
 * 체크 상태는 app_id 와 함께 저장한다.
 * 저장된 app_id 가 지금 정책과 다르면 버린다 — 다른 서비스에서 찍은 체크가 넘어오면 안 된다.
 */
function loadChecks(appId: string): Checks {
    try {
        const raw = JSON.parse(sessionStorage.getItem(CHECKS_SS_KEY) || "null");
        if (!raw || raw.app_id !== appId) return {};
        return (raw.checks || {}) as Checks;
    } catch {
        return {};
    }
}
function saveChecks(appId: string, checks: Checks, documentKey?: string) {
    sessionStorage.setItem(CHECKS_SS_KEY, JSON.stringify({ app_id: appId, checks, documentKey }));
}

export default function AgreeTermsPage() {
    const nav = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { s, spx } = useScale();

    const returnUrl = searchParams.get("return_url") || (location.state as any)?.returnUrl || "";

    // app_id: URL 쿼리 → 세션 → 기본값(booktalk).
    //
    // 세션에 남기는 이유: 이 화면을 떠났다 돌아오는 경로(전문보기 등)에서 쿼리가 날아간다.
    // 다만 폴백을 무조건 쓰면 한 번 ?app_id=X 로 들어온 탭이 계속 X 로 남으므로,
    // 쿼리가 없고 복귀 신호(state.agreed)도 없으면 새 진입으로 보고 세션을 비운다.
    const appId = useMemo(() => {
        const fromQuery = (searchParams.get("app_id") || "").trim();
        if (fromQuery) {
            sessionStorage.setItem(APP_ID_SS_KEY, fromQuery);
            return fromQuery;
        }
        if ((location.state as any)?.agreed) {
            return (sessionStorage.getItem(APP_ID_SS_KEY) || "").trim();
        }
        sessionStorage.removeItem(APP_ID_SS_KEY);
        return "";
    }, [searchParams, location.state]);

    const { policy: basePolicy } = useMemo(() => resolveConsentPolicy(appId), [appId]);
    const isPagebox = basePolicy.app_id === "pagebox";
    const qrEntry = isPagebox && searchParams.get("entry") === "qr";
    const { documents, error: legalError, retry: retryLegal } = usePageboxLegal(isPagebox);
    const documentKey = documents ? JSON.stringify([
        documents.terms.version, documents.terms.updatedAt,
        documents.privacy.version, documents.privacy.updatedAt,
    ]) : undefined;
    const [acceptedDocumentKey, setAcceptedDocumentKey] = useState<string>();
    const policy = useMemo(() => !isPagebox || !documents ? basePolicy : ({
        ...basePolicy,
        items: basePolicy.items.map(item => ({ ...item, version: documents[item.code as 'terms' | 'privacy'].version })),
    }), [basePolicy, isPagebox, documents]);
    const legalReady = !isPagebox || (!!documents && acceptedDocumentKey === documentKey);

    const [checks, setChecks] = useState<Checks>(() => loadChecks(policy.app_id));

    useEffect(() => {
        if (!isPagebox || !documentKey) return;
        let next: Checks = {};
        try {
            const saved = JSON.parse(sessionStorage.getItem(CHECKS_SS_KEY) || "null");
            if (saved?.app_id === "pagebox" && saved.documentKey === documentKey) next = saved.checks || {};
        } catch { /* Invalid saved state requires fresh consent. */ }
        setChecks(next);
        saveChecks("pagebox", next, documentKey);
        setAcceptedDocumentKey(documentKey);
    }, [isPagebox, documentKey]);

    const setOne = (code: string, v: boolean) => {
        if (!legalReady) return;
        setChecks((prev) => {
            const next = { ...prev, [code]: v };
            saveChecks(policy.app_id, next, documentKey);
            return next;
        });
    };

    useEffect(() => {
        const agreed = (location.state as { agreed?: string } | null)?.agreed;
        if (!agreed) return;
        if (!policy.items.some((i) => i.code === agreed)) return;
        setOne(agreed, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, policy]);

    // "전체 동의" 는 실제로 렌더된 항목만 대상으로 한다.
    // (숨겨진 항목이 딸려 들어가면 본 적 없는 항목에 동의하게 된다)
    const allOn = useMemo(
        () => policy.items.length > 0 && policy.items.every((i) => !!checks[i.code]),
        [policy, checks],
    );

    const canNext = useMemo(
        () => legalReady && policy.items.filter((i) => i.required).every((i) => !!checks[i.code]),
        [policy, checks, legalReady],
    );

    const toggleAll = (next: boolean) => {
        if (!legalReady) return;
        const v: Checks = { ...checks };
        policy.items.forEach((i) => { v[i.code] = next; });
        setChecks(v);
        saveChecks(policy.app_id, v, documentKey);
    };

    const goNext = () => {
        if (!canNext) return;
        const agreements = buildAgreements(policy, checks);
        const fromEvent = !!(location.state as any)?.fromEvent;
        // 체크 상태는 여기서 지우지 않는다 — 가입 화면에서 뒤로 오면 그대로 복원돼야 한다.
        // 정리는 가입 성공 시점(RegisterPage)에서 한 번만.
        sessionStorage.setItem("bt.agreements", JSON.stringify(agreements));
        nav(policy.app_id === "pagebox" ? pageboxAuthPath("/auth/register", qrEntry) : "/auth/register", {
            state: { agreements, fromEvent, returnUrl, appId: policy.app_id },
            replace: true,
        });
    };

    // PageBox 는 외부에서 ?app_id=pagebox 링크로 이 화면에 바로 들어온다.
    // 앞 화면이 없어서 nav(-1) 이 SPA 밖으로 튕겨나가므로 뒤로가기를 그리지 않는다.
    // (모르는 app_id 는 booktalk 정책으로 떨어지므로 policy.app_id 는 실제 pagebox 일 때만 맞는다)
    const showBack = policy.app_id !== "pagebox";

    const renderRow = (item: ConsentItem) => {
        // PageBox keeps the document inside this WebView. Booktalk retains its existing external links.
        const right = isPagebox
            ? <button type="button" disabled={!legalReady} onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set("document", item.code);
                nav({ pathname: location.pathname, search: params.toString() }, { state: location.state });
                window.scrollTo(0, 0);
            }} style={{ fontSize: spx(18), textDecoration: "underline", color: "inherit", background: "transparent", border: 0, padding: spx(8) }}>전문보기</button>
            : item.legalUrl
            ? (
                <a
                    href={item.legalUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: spx(18), textDecoration: "underline", color: "inherit" }}
                >
                    전문보기
                </a>
            )
            : item.note
                ? <span style={{ fontSize: spx(18), color: "#666" }}>{item.note}</span>
                : undefined;

        return (
            <CheckRow
                key={item.code}
                id={item.code}
                checked={!!checks[item.code]}
                onChange={(v) => setOne(item.code, v)}
                borderTop
                label={<span>[{item.required ? "필수" : "선택"}] {item.label}</span>}
                right={right}
                padY={s(BASE.ROW_PAD_Y)}
                fontPx={s(28)}
            />
        );
    };

    const selectedDocument = searchParams.get("document");
    if (isPagebox && documents && (selectedDocument === "terms" || selectedDocument === "privacy")) {
        return <PageboxLegalViewer document={documents[selectedDocument]} width={spx(BASE.W)} fontSize={spx(28)} onClose={() => {
            const params = new URLSearchParams(searchParams);
            params.delete("document");
            nav({ pathname: location.pathname, search: params.toString() }, { replace: true, state: location.state });
            window.scrollTo(0, 0);
        }} />;
    }

    return (
        <PageShell
            title="이용 동의"
            onBack={showBack ? () => nav(-1) : undefined}
            backIcon={showBack ? withBase("icons/prev1.svg") : undefined}
            width={spx(BASE.W)}
        >
            <div style={{ width: spx(BASE.BLOCK_W), margin: "0 auto", paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}>
                <h1 style={{ fontSize: spx(BASE.TITLE_FZ), margin: 0, marginBottom: spx(16), fontWeight: 800 }}>이용 동의</h1>

                {policy.app_id === "pagebox" && <PageboxAccountNotice fontSize={spx(26)} login qrEntry={qrEntry} />}

                {isPagebox && !legalReady && <div role="status" style={{ fontSize: spx(24), marginBottom: spx(16) }}>
                    {legalError ? <>약관을 불러오지 못했습니다. 연결 상태를 확인해 주세요. <button type="button" onClick={retryLegal} style={{ fontSize: "inherit" }}>다시 시도</button></> : "약관을 불러오는 중입니다."}
                </div>}
                <CheckRow id="all" checked={allOn} onChange={toggleAll} label={"전체 동의"} padY={s(BASE.ROW_PAD_Y)} fontPx={s(32)} />
                <div style={{ height: 1, background: "#e5e5e5" }} />

                {policy.items.map(renderRow)}

                <div style={{ display: "grid", placeItems: "center", marginTop: spx(BASE.GAP_Y * 2), marginBottom: spx(40) }}>
                    <button
                        disabled={!canNext}
                        onClick={goNext}
                        style={{
                            width: spx(BASE.BTN_W), height: spx(BASE.BTN_H), borderRadius: spx(12),
                            background: "#000", color: "#fff", fontWeight: 800, fontSize: spx(32),
                            opacity: canNext ? 1 : 0.5, border: "none",
                        }}
                    >
                        다음
                    </button>
                </div>
            </div>
        </PageShell>
    );
}